<script setup>
import { ref, onMounted, watch } from 'vue';

defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'created']);

const { addToast } = useToast();
const { createReview, fetchAvailableShops } = useReviewApi();

// Form States
const availableShops = ref([]);
const isShopsLoading = ref(false);
const selectedShopMode = ref('custom'); // 'existing' | 'custom'
const selectedShopId = ref('');
const customRestaurantName = ref('');
const address = ref('');
const rating = ref(5);
const details = ref('');
const tags = ref(['Burger']);
const newTagInput = ref('');
const imageFiles = ref([]); // [{ file, previewUrl }]
const isSubmitting = ref(false);

const popularFoodTags = [
  'Burger',
  'Pizza',
  'Biryani',
  'Pasta',
  'Coffee',
  'Dessert',
  'Chinese',
  'Fast Food',
  'Buffet',
  'Steak',
  'Kebab',
  'Cafe',
];

onMounted(async () => {
  isShopsLoading.value = true;
  availableShops.value = await fetchAvailableShops();
  isShopsLoading.value = false;
});

// Watch shop selection to autofill name & address
watch(selectedShopId, (newId) => {
  if (newId) {
    const shop = availableShops.value.find((s) => s.id === Number(newId));
    if (shop) {
      if (shop.address && !address.value) {
        address.value = shop.address;
      }
    }
  }
});

// Tag Helpers
const addTag = (tagToAdd) => {
  const clean = tagToAdd.trim().replace(/^#/, '');
  if (!clean) return;
  if (tags.value.length >= 10) {
    addToast('You can add up to 10 tags', 'alert-warning');
    return;
  }
  if (!tags.value.includes(clean)) {
    tags.value.push(clean);
  }
  newTagInput.value = '';
};

const removeTag = (idx) => {
  tags.value.splice(idx, 1);
};

const handleTagKeydown = (e) => {
  if (e.key === 'Enter' || e.key === ',') {
    e.preventDefault();
    addTag(newTagInput.value);
  }
};

// Image Upload Handlers
const handleFileUpload = (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  if (imageFiles.value.length + files.length > 5) {
    addToast('You can upload a maximum of 5 images', 'alert-warning');
    return;
  }

  for (const file of files) {
    if (file.size > 5 * 1024 * 1024) {
      addToast(`Image "${file.name}" exceeds 5MB limit`, 'alert-error');
      continue;
    }
    const previewUrl = URL.createObjectURL(file);
    imageFiles.value.push({ file, previewUrl });
  }

  // Reset input
  e.target.value = '';
};

const removeImage = (index) => {
  URL.revokeObjectURL(imageFiles.value[index].previewUrl);
  imageFiles.value.splice(index, 1);
};

// Form Reset
const resetForm = () => {
  selectedShopMode.value = 'custom';
  selectedShopId.value = '';
  customRestaurantName.value = '';
  address.value = '';
  rating.value = 5;
  details.value = '';
  tags.value = ['Burger'];
  newTagInput.value = '';
  imageFiles.value.forEach((img) => URL.revokeObjectURL(img.previewUrl));
  imageFiles.value = [];
};

// Submit Handler
const handleSubmit = async () => {
  // Validations
  if (selectedShopMode.value === 'existing' && !selectedShopId.value) {
    addToast('Please select a restaurant', 'alert-warning');
    return;
  }
  if (selectedShopMode.value === 'custom' && !customRestaurantName.value.trim()) {
    addToast('Please enter the restaurant name', 'alert-warning');
    return;
  }
  if (!address.value.trim()) {
    addToast('Please enter an address or Google Maps location', 'alert-warning');
    return;
  }
  if (!details.value.trim()) {
    addToast('Please write your review details', 'alert-warning');
    return;
  }
  if (tags.value.length === 0) {
    addToast('Please add at least one food tag (e.g. Burger)', 'alert-warning');
    return;
  }

  isSubmitting.value = true;
  try {
    const formData = new FormData();

    if (selectedShopMode.value === 'existing' && selectedShopId.value) {
      formData.append('shop_id', selectedShopId.value);
    } else {
      formData.append('restaurant_name', customRestaurantName.value.trim());
    }

    formData.append('address', address.value.trim());
    formData.append('details', details.value.trim());
    formData.append('rating', rating.value);

    // Append tags array
    tags.value.forEach((tag, index) => {
      formData.append(`tags[${index}]`, tag);
    });

    // Append images
    imageFiles.value.forEach((img) => {
      formData.append('images[]', img.file);
    });

    const response = await createReview(formData);
    addToast('Food review posted successfully! 🎉', 'alert-success');
    emit('created', response.data);
    resetForm();
    emit('close');
  } catch (err) {
    const msg = err.data?.message || (err.data?.errors ? Object.values(err.data.errors).flat().join(', ') : 'Failed to post review');
    addToast(msg, 'alert-error');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <dialog v-if="isOpen" class="modal modal-open backdrop-blur-sm z-50 p-2 sm:p-4">
    <div class="modal-box max-w-xl sm:max-w-2xl w-full p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-base-200 shadow-2xl bg-base-100 max-h-[90vh] overflow-y-auto">
      <!-- Modal Header -->
      <div class="flex items-center justify-between pb-3 sm:pb-4 border-b border-base-200">
        <div class="flex items-center gap-2.5 sm:gap-3">
          <div class="w-9 h-9 sm:w-11 sm:h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl sm:text-2xl shrink-0">
            🍔
          </div>
          <div>
            <h2 class="text-lg sm:text-xl md:text-2xl font-bold text-base-content leading-tight">Share Food Review</h2>
            <p class="text-[11px] sm:text-xs text-base-content/60">Inspire fellow food lovers with your experience</p>
          </div>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-circle btn-sm text-base-content/70"
          :disabled="isSubmitting"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <form class="space-y-3.5 sm:space-y-4 mt-3 sm:mt-4" @submit.prevent="handleSubmit">
        <!-- Restaurant Selection Toggle -->
        <div class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Restaurant Type</span></label>
          <div class="join w-full grid grid-cols-2 rounded-xl overflow-hidden border border-base-300">
            <input
              v-model="selectedShopMode"
              class="join-item btn btn-sm text-xs sm:text-sm font-semibold border-none"
              :class="selectedShopMode === 'existing' ? 'btn-primary text-white' : 'btn-ghost bg-base-200/50'"
              type="radio"
              name="shop_mode"
              aria-label="Partner Restaurant"
              value="existing"
            >
            <input
              v-model="selectedShopMode"
              class="join-item btn btn-sm text-xs sm:text-sm font-semibold border-none"
              :class="selectedShopMode === 'custom' ? 'btn-primary text-white' : 'btn-ghost bg-base-200/50'"
              type="radio"
              name="shop_mode"
              aria-label="Other / Any Place"
              value="custom"
            >
          </div>
        </div>

        <!-- Existing Shop Dropdown -->
        <div v-if="selectedShopMode === 'existing'" class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Select Partner Restaurant *</span></label>
          <select
            v-model="selectedShopId"
            class="select select-bordered select-sm sm:select-md w-full rounded-xl sm:rounded-2xl text-xs sm:text-sm focus:ring-2 ring-primary/20"
            required
          >
            <option value="" disabled>Choose a restaurant...</option>
            <option v-for="shop in availableShops" :key="shop.id" :value="shop.id">
              {{ shop.name }} ({{ shop.address || 'Dhaka' }})
            </option>
          </select>
        </div>

        <!-- Custom Restaurant Name -->
        <div v-else class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Restaurant Name *</span></label>
          <input
            v-model="customRestaurantName"
            type="text"
            placeholder="e.g. Madchef, Takeout, Chillox, Sultan's Dine"
            maxlength="255"
            class="input input-bordered input-sm sm:input-md rounded-xl sm:rounded-2xl text-xs sm:text-sm focus:ring-2 ring-primary/20"
            required
          >
        </div>

        <!-- Address or Google Map URL -->
        <div class="form-control">
          <label class="label py-1">
            <span class="label-text font-semibold text-xs text-base-content/80">Address or Google Maps URL *</span>
          </label>
          <input
            v-model="address"
            type="text"
            placeholder="e.g. House 12, Road 4, Banani or https://maps.app.goo.gl/..."
            maxlength="500"
            class="input input-bordered input-sm sm:input-md rounded-xl sm:rounded-2xl text-xs sm:text-sm focus:ring-2 ring-primary/20"
            required
          >
        </div>

        <!-- Rating Star Selector -->
        <div class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Your Rating</span></label>
          <div class="flex items-center gap-3 p-2 bg-base-200/40 rounded-xl sm:rounded-2xl border border-base-200">
            <div class="rating rating-md sm:rating-lg gap-1">
              <input
                v-for="star in 5"
                :key="star"
                v-model="rating"
                type="radio"
                name="rating-create"
                class="mask mask-star-2 bg-amber-400"
                :value="star"
              >
            </div>
            <span class="text-xs sm:text-sm font-bold text-amber-500">{{ rating }} of 5 Stars</span>
          </div>
        </div>

        <!-- Tags Selection -->
        <div class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Food Tags (1-10 tags) *</span></label>
          
          <!-- Popular Tags Pill Picker -->
          <div class="flex flex-wrap gap-1.5 mb-2">
            <button
              v-for="t in popularFoodTags"
              :key="t"
              type="button"
              class="badge badge-sm py-1.5 px-2.5 rounded-lg cursor-pointer transition-all text-xs"
              :class="tags.includes(t) ? 'badge-primary text-primary-content font-bold shadow-xs' : 'badge-ghost bg-base-200/60 hover:bg-base-200 text-base-content/70'"
              @click="tags.includes(t) ? removeTag(tags.indexOf(t)) : addTag(t)"
            >
              {{ tags.includes(t) ? '✓ ' : '+ ' }}{{ t }}
            </button>
          </div>

          <!-- Active Tags & Input Box -->
          <div class="flex flex-wrap items-center gap-1.5 p-2 sm:p-2.5 bg-base-200/40 rounded-xl sm:rounded-2xl border border-base-200 min-h-[44px]">
            <span
              v-for="(tag, idx) in tags"
              :key="idx"
              class="badge badge-primary badge-sm gap-1 py-2 px-2.5 font-medium text-xs rounded-lg"
            >
              #{{ tag }}
              <button type="button" class="hover:text-error ml-0.5" @click="removeTag(idx)">✕</button>
            </span>
            <input
              v-model="newTagInput"
              type="text"
              placeholder="Add custom tag + Enter..."
              class="input input-ghost input-xs flex-1 min-w-[130px] text-xs focus:outline-none bg-transparent"
              @keydown="handleTagKeydown"
            >
          </div>
        </div>

        <!-- Review Text Details -->
        <div class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Review Details *</span></label>
          <textarea
            v-model="details"
            rows="3"
            placeholder="Tell us about the taste, portion, price, service, ambiance, and must-try dishes..."
            maxlength="5000"
            class="textarea textarea-bordered rounded-xl sm:rounded-2xl text-xs sm:text-sm focus:ring-2 ring-primary/20 leading-relaxed"
            required
          />
          <label class="label py-0.5">
            <span class="label-text-alt text-base-content/50 text-[10px] sm:text-xs">{{ details.length }}/5000 characters</span>
          </label>
        </div>

        <!-- Image Upload Section -->
        <div class="form-control">
          <label class="label py-1"><span class="label-text font-semibold text-xs text-base-content/80">Food Photos (Up to 5 images, max 5MB each)</span></label>
          
          <div class="flex flex-wrap gap-2.5">
            <!-- Uploaded Image Previews -->
            <div
              v-for="(img, idx) in imageFiles"
              :key="idx"
              class="relative w-18 h-18 sm:w-22 sm:h-22 rounded-xl sm:rounded-2xl overflow-hidden border border-base-300 group shadow-xs shrink-0"
            >
              <img :src="img.previewUrl" alt="Upload preview" class="w-full h-full object-cover">
              <button
                type="button"
                class="btn btn-circle btn-xs btn-error absolute top-1 right-1 text-white shadow-md"
                title="Remove photo"
                @click="removeImage(idx)"
              >
                ✕
              </button>
            </div>

            <!-- Upload Button Tile -->
            <label
              v-if="imageFiles.length < 5"
              class="w-18 h-18 sm:w-22 sm:h-22 rounded-xl sm:rounded-2xl border-2 border-dashed border-base-300 hover:border-primary flex flex-col items-center justify-center cursor-pointer transition-colors text-base-content/60 hover:text-primary bg-base-200/40 shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              <span class="text-[10px] sm:text-xs font-semibold mt-1">Add Photo</span>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/jpg,image/webp"
                class="hidden"
                @change="handleFileUpload"
              >
            </label>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="modal-action flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-base-200">
          <button
            type="button"
            class="btn btn-ghost btn-sm sm:btn-md rounded-xl sm:rounded-2xl w-full sm:w-auto"
            :disabled="isSubmitting"
            @click="emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary text-white btn-sm sm:btn-md px-6 sm:px-8 rounded-xl sm:rounded-2xl w-full sm:w-auto shadow-md shadow-primary/20"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="loading loading-spinner loading-xs" />
            <span v-else>Publish Review 🚀</span>
          </button>
        </div>
      </form>
    </div>
    <form method="dialog" class="modal-backdrop" @click="emit('close')">
      <button>close</button>
    </form>
  </dialog>
</template>
